import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Input,
  Select,
  Form,
  message,
  Modal,
} from 'antd';
import ModalWrapper from './ModalWrapper';
import DebounceSelect from './DebounceSelect';
import { fetchAssigneeOptions } from './assigneeSearch';
import { assignModalTitle, assignmentStaysOpenNote, fetchArchiveClosesAssignments } from '../../global/featureFlags';

const { confirm } = Modal;

const { TextArea } = Input;
class AssignRecord extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDocumentStatus: null,
      sendToken: props.modalType.data.record,
      parentType: props.modalType.data.receiverType,
      markComplete: props.modalType.data.markComplete || false,
      assignedDocumentId: props.modalType.data.assignedDocumentId || null,
      assignees: null,
      documentStatuses: [],
      note: '',
      // Tri-state: null until the setting answers. NOT false — the note is a
      // categorical claim about archive behavior, and starting it at false made the
      // modal assert "archiving does not close it" on the first paint to
      // organizations where archiving does close it, then indefinitely whenever the
      // settings request failed. Absent-endpoint still resolves to false and still
      // shows the note; see `fetchArchiveClosesAssignments`.
      archiveClosesAssignments: null,
    };
    this.mounted = false;
  }

  componentDidMount = async () => {
    const { getDocumentStatuses } = this.props;
    this.mounted = true;

    // Not awaited with the statuses: the note is the only thing it decides, and
    // holding the whole modal on it would delay the fields for a line of prose.
    fetchArchiveClosesAssignments().then((archiveClosesAssignments) => {
      if (this.mounted) this.setState({ archiveClosesAssignments });
    });

    await getDocumentStatuses();
    if (!this.mounted) return;
    this.setState({ documentStatuses: this.props.allDocumentStatuses.document_statuses });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  fetchUserList = async (value) => {
    const { getUserDepartmentsByQuery } = this.props;
    const assignees = await fetchAssigneeOptions({ value, getUserDepartmentsByQuery });

    this.setState({ assignees });
    return assignees;
  }

  handleMarkCompleted = async () => {
    const { markDocumentAsCompleted } = this.props;
    const {
      assignedDocumentId,
      markComplete,
    } = this.state;

    if (markComplete) {
      await markDocumentAsCompleted(assignedDocumentId);

      if (window.location.pathname.includes('/app/view/')) {
        message.success({
          content: 'Document Assigned and Marked Complete Successfully. You\'ll be redirected to the assigned records page.',
        });

        // After 3 seconds, go to assigned records page.
        setTimeout(() => { this.context.router.history.push('/app/assigned'); }, 3000);
      } else {
        message.success({
          content: 'Document Assigned and Marked Complete Successfully',
        });

        window.location.reload();
      }
    } else {
      message.success({
        content: 'Document Assigned Successfully',
      });
    }
  }

  confirmAssign = async (assignedDocument) => {
    const { assignDocument } = this.props;
    await assignDocument(assignedDocument);

    const { error, hideModal } = this.props;
    const errorMessageContent = 'Error assigning document. Please try again.';
    if (!error) {
      hideModal();
      this.handleMarkCompleted();
    } else {
      hideModal();
      message.error({
        content: errorMessageContent,
      });
    }
  }

  assignDocument = async () => {
    const { assignDocument } = this.props;
    const {
      sendToken,
      assignee,
      note,
      selectedDocumentStatus,
      parentType,
    } = this.state;
    let parent = '';
    if (parentType === 'received_fax') {
      parent = 'ReceivedFax';
    } else if (parentType === 'receiver') {
      parent = 'Receiver';
    } else {
      parent = 'EmailAttachment';
    }

    let assignedDocument = {
      send_token: sendToken,
      note,
      assignee_id: assignee.value,
      parent_type: parent,
      document_status_id: selectedDocumentStatus,
      force_assign: false,
    };
    await assignDocument(assignedDocument);

    const { error, hideModal, isDepartmentUser } = this.props;
    const errorMessageContent = 'Error assigning document. Please try again.';
    if (!error) {
      if (!isDepartmentUser) {
        confirm({
          title: 'This person doesn\'t have access!',
          okText: 'Yes',
          cancelText: 'No',
          content: 'This user doesn\'t currently have access to the document. Would you like to grant them access to complete this assignment?',
          onOk: () => {
            assignedDocument.force_assign = true;
            return this.confirmAssign(assignedDocument);
          },
          onCancel: () => {
            return;
          },
        });
      } else {
        hideModal();
        this.handleMarkCompleted();
      }
    } else {
      hideModal();
      message.error({
        content: errorMessageContent,
      });
    }
  }

  renderModal = () => {
    const { isLoading } = this.props;
    const {
      documentStatuses,
      selectedDocumentStatus,
      assignee,
      assignees,
      markComplete,
      archiveClosesAssignments,
    } = this.state;

    const handleAssignToChange = (e) => {
      const assignee = this.state.assignees.find(user => user.value === e.value);
      this.setState({ assignee: assignee });
    };

    const handleChange = (value) => {
      this.setState({ selectedDocumentStatus: value });
    };

    if (isLoading) {
      return (<span> Assigning Record, Please wait... </span>);
    }

    // Both halves of the sentence have to actually be true here.
    //
    // Mark Complete and Reassign (the assigned-records row action, the record
    // viewer's overflow menu and MarkCompleteDropdown all open this same modal with
    // `markComplete`) closes the current assignment on submit — set by the clicker,
    // not the assignee — creates a new one for the new assignee, and drops the record
    // out of /app/assigned. "The record stays in this inbox. The assignee closes the
    // assignment themselves" is false on both halves for the assignment on screen, so
    // that flow gets no note at all.
    //
    // And with archiving closing assignments, the last clause is false too.
    //
    // Only an answered `false` earns the note. `true` makes it wrong, and `null` —
    // the request still in flight, or failed — means we have no answer to base it on,
    // so the modal says nothing rather than guessing. `markComplete` suppresses it
    // either way, whatever the setting turns out to be.
    const archivingLeavesAssignmentsOpen = archiveClosesAssignments === false;
    const staysOpenNote = (!markComplete && archivingLeavesAssignmentsOpen)
      ? assignmentStaysOpenNote()
      : null;

    return (
      <React.Fragment>
        {staysOpenNote && (
          <p style={{ marginBottom: '12px' }}>
            {staysOpenNote}
          </p>
        )}
        <Form
          onFinish={this.assignDocument}
          id="notifyUser"
        >
          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Item
              layout="vertical"
              onFinish={this.update}
              id="updateDocumentStatus"
              style={{ flex: 0.5 }}
            >
              Document Status:
              <Select
                onChange={handleChange}
                placeholder="Document Status"
                value={selectedDocumentStatus}
              >
                {documentStatuses.map(docStatus => <Select.Option key={docStatus.id} value={docStatus.id}>{docStatus.name}</Select.Option>)}
              </Select>
            </Form.Item>
            <Form.Item
              name="Assigned_to"
              style={{ flex: 0.5 }}
            >
              Medsender User to Assign To:
              <DebounceSelect
                showSearch
                allowClear
                value={assignee}
                placeholder="Search for Medsender users"
                fetchOptions={this.fetchUserList}
                onChange={handleAssignToChange}
                options={(assignees || []).map(user => ({
                  value: user[0],
                  label: user[1],
                }))}
              />
            </Form.Item>
          </div>
          <Form.Item
            hasFeedback
            name="note"
            style={{ flex: 1 }}
          >
            Note:
            <TextArea
              onChange={event => this.setState({ note: event.target.value })}
              rows={2}
            />
          </Form.Item>
        </Form>
      </React.Fragment>
    );
  }

  render() {
    const { isLoading, hideModal } = this.props;
    const { selectedDocumentStatus, assignee, markComplete } = this.state;
    const customContentStyle = {
      width: '340px',
    };

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.assignDocument}
        actionName="Submit"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={isLoading || !selectedDocumentStatus || !assignee}
        modalTitle={assignModalTitle({ markComplete })}
        form="assignDocument"
      >
        {this.renderModal()}
      </ModalWrapper>
    );
  }
}

AssignRecord.contextTypes = {
  router: PropTypes.object.isRequired,
};

AssignRecord.defaultProps = {
  error: false,
  errorCode: null,
};

AssignRecord.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  assignDocument: PropTypes.func.isRequired,
  markDocumentAsCompleted: PropTypes.func.isRequired,
  allDocumentStatuses: PropTypes.object.isRequired,
  getDocumentStatuses: PropTypes.func.isRequired,
  getUserDepartmentsByQuery: PropTypes.func.isRequired,
  error: PropTypes.bool,
  isDepartmentUser: PropTypes.bool,
};

export default connect(state => ({
  isLoading: state.records.isLoading,
  error: state.records.error,
  errorCode: state.records.errorCode,
  allDocumentStatuses: state.userProfile.allDocumentStatuses,
  isDepartmentUser: state.records.isDepartmentUser,
}))(AssignRecord);
